import {assert, assertAndContinue} from 'Asserts';
import {convertDataTableToTable} from 'notebook/data-tables/DataTableUtil';
import inspectAllNoteParts from 'notebook/notes/util/inspectAllNoteParts';
import {convertRegistrationTableToTable} from 'notebook/registration-tables/RegistrationTableNotePartUtil';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import notePartToTableDatas, {
  allTableTypes,
  CollectibleLegacyTableType,
  CollectibleTableData,
} from 'notebook/structured-tables/util/notePartToTableDatas';
import {Table} from 'notebook/tables/TableConstants';
import {
  convertTransferTableToTable,
  TransferTableForConvertToTable,
} from 'notebook/transfer-tables/TransferTableNotePartUtil';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import {assertNever} from 'util/Types';

// Exported for unit testing only.
export function _convertTable(entryId: string, tableData: CollectibleTableData): Table | null {
  if (tableData.isStructuredTable) {
    const adapter = getStructuredTableAdapters()[tableData.table.kind];
    assert(adapter != null, `Unknown structured table adapter`, {tableData});
    return adapter.toTable(tableData.table);
  }

  switch (tableData.legacyTableType) {
    case CollectibleLegacyTableType.LEGACY_REGISTRATION_TABLE:
      return convertRegistrationTableToTable(tableData.table, entryId, []);

    case CollectibleLegacyTableType.LEGACY_RESULT_TABLE:
      return convertDataTableToTable(tableData.table);

    case CollectibleLegacyTableType.LEGACY_TRANSFER_TABLE: {
      const transferTableModel = tableData.table;
      if (transferTableModel.containerSchema == null) {
        return null;
      }
      const table: TransferTableForConvertToTable = {
        ...transferTableModel,
        containerSchema: transferTableModel.containerSchema,
      };
      return convertTransferTableToTable(table, []);
    }

    default:
      assertAndContinue(false, 'Table type without a converter', {
        tableType: assertNever(tableData).tableType,
      });
      return null;
  }
}

export function getTablesInNote(noteParts: Array<NotePart>): Array<CollectibleTableData> {
  const tableDatas: Array<CollectibleTableData> = [];
  inspectAllNoteParts(noteParts, (notePart) => {
    const tablesInNotePart = notePartToTableDatas(notePart, allTableTypes);
    if (tablesInNotePart != null) {
      tableDatas.push(...tablesInNotePart);
    }
  });
  return tableDatas;
}

/**
 * Extracts all of the tables in the note, and converts them into the shared `Table` type.
 */
export function getTablesInNoteAsTables(noteParts: Array<NotePart>, entryId: string): Array<Table> {
  const collectedTables = getTablesInNote(noteParts);
  const convertedTables: Array<Table> = [];
  for (const tableData of collectedTables) {
    const table = _convertTable(entryId, tableData);
    if (table != null) {
      convertedTables.push(table);
    }
  }
  return convertedTables;
}
